/**
 * Zeitabschnitte für die Balkenanzeige: Wie viel Zeit steht man aktiv in der
 * Küche, wie lange wartet man (Kochen, Backen, Ruhen, Kühlen)?
 */
import type { RecipeTimes } from './types.ts';

/** Ein Abschnitt des Zeitbalkens. */
export interface TimeSegment {
  readonly key: string;
  readonly label: string;
  readonly minutes: number;
  /** Aktiv heißt: Man muss dabei sein. */
  readonly active: boolean;
}

/** Zeitarten eines Rezepts in der Reihenfolge des Ablaufs. */
export const TIME_PARTS: readonly { key: keyof RecipeTimes; label: string; active: boolean }[] = [
  { key: 'preparationMinutes', label: 'Vorbereitung', active: true },
  { key: 'activeMinutes', label: 'Zubereitung', active: true },
  { key: 'cookingMinutes', label: 'Kochen', active: false },
  { key: 'bakingMinutes', label: 'Backen', active: false },
  { key: 'restingMinutes', label: 'Ruhen', active: false },
  { key: 'coolingMinutes', label: 'Kühlen', active: false },
];

/** Abschnitte aus den Zeiten eines Rezepts; leere Abschnitte fallen weg. */
export function timeSegments(times: RecipeTimes): TimeSegment[] {
  return TIME_PARTS.map((part) => ({
    key: part.key,
    label: part.label,
    minutes: Math.max(0, times[part.key]),
    active: part.active,
  })).filter((segment) => segment.minutes > 0);
}

/** Was von einem Schritt für den Ablauf gebraucht wird. */
export interface StepTiming {
  readonly title: string;
  readonly durationMinutes: number | null;
  readonly passive: boolean;
}

/**
 * Abschnitte aus den Schritten, nacheinander. Schritte ohne Dauer fallen
 * weg; ihr Titel ist „Schritt n“, wenn keiner angegeben ist.
 */
export function stepSegments(steps: readonly StepTiming[]): TimeSegment[] {
  return steps
    .map((step, index) => ({
      key: `step-${index}`,
      label: step.title.trim() === '' ? `Schritt ${index + 1}` : step.title.trim(),
      minutes: Math.max(0, step.durationMinutes ?? 0),
      active: !step.passive,
    }))
    .filter((segment) => segment.minutes > 0);
}

/** Summe aktiver und passiver Minuten. */
export function segmentTotals(segments: readonly TimeSegment[]): { active: number; passive: number } {
  return segments.reduce(
    (sum, segment) =>
      segment.active
        ? { ...sum, active: sum.active + segment.minutes }
        : { ...sum, passive: sum.passive + segment.minutes },
    { active: 0, passive: 0 },
  );
}
